import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt, 
  faClock 
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { contattiAzienda, numeriServizioClienti } from '../utils/data';
import ContactForm from '../components/ContactForm';

const Contattaci: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-20 left-20 w-[400px] h-[400px] bg-secondary rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-10 w-[500px] h-[500px] bg-accent rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">Parliamone</span>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 font-display">Contattaci</h1>
            <p className="text-xl text-white/75 mb-3">
              Richiedi una consulenza gratuita e senza impegno. Siamo qui per aiutarti a confrontare le offerte.
            </p>
            <p className="text-sm text-white/50">
              Il nostro servizio è gratuito. Nessun obbligo di sottoscrizione.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="py-24">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Contact Info */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/3"
            >
              <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">Come trovarci</span>
              <h2 className="text-3xl font-extrabold mb-8 text-light-text font-display">Informazioni di Contatto</h2>
              
              <div className="space-y-4">
                {/* Numeri Servizio Clienti */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="p-5 rounded-2xl bg-white border border-light-border/40 shadow-card"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
                      <FontAwesomeIcon icon={faPhone} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-light-text font-display">Consulenza Gratuita</h3>
                      <p className="text-xs text-light-muted">Chiamata senza impegno</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 pl-[52px]">
                    {numeriServizioClienti.map((item) => (
                      <a 
                        key={item.tel}
                        href={`tel:${item.tel}`} 
                        className="text-primary hover:text-secondary text-sm font-semibold transition-colors inline-flex items-center gap-2"
                      >
                        {item.numero}
                      </a>
                    ))}
                  </div>
                </motion.div>

                {/* Email */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="flex items-start p-5 rounded-2xl bg-white border border-light-border/40 shadow-card"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
                    <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold mb-1 text-light-text font-display">Email</h3>
                    <a href={`mailto:${contattiAzienda.email}`} className="text-primary hover:text-secondary text-sm font-medium transition-colors">{contattiAzienda.email}</a>
                  </div>
                </motion.div>

                {/* Sede */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="flex items-start p-5 rounded-2xl bg-white border border-light-border/40 shadow-card"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold mb-1 text-light-text font-display">Sede Legale</h3>
                    <p className="text-light-muted text-sm">{contattiAzienda.indirizzo}</p>
                  </div>
                </motion.div>

                {/* Orari */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="flex items-start p-5 rounded-2xl bg-white border border-light-border/40 shadow-card"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
                    <FontAwesomeIcon icon={faClock} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold mb-2 text-light-text font-display">Orari di Apertura</h3>
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-light-muted">Lun - Ven:</span>
                        <span className="text-light-text font-medium">{contattiAzienda.orarioLavorativo}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-light-muted">Sabato:</span>
                        <span className="text-light-text font-medium">{contattiAzienda.orarioSabato}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-light-muted">Domenica:</span>
                        <span className="text-light-text font-medium">{contattiAzienda.orarioDomenica}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contattaci;
